import React, { useState, useEffect, useCallback } from "react";
import { CircularProgress } from "@mui/material";
import { Doughnut } from "react-chartjs-2";
import { getPaymentMethodChartDataAction } from "../../Actions/TransactionActions";
import { getPayCentralPaymentMethodChartDataAction, getCombinedPaymentMethodChartDataAction } from "../../Actions/PaycentralAction";
import NoDataFound from "./NoDataFound";
import { MyCancelIcon } from "../../Assets/svg/MyIcons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const DashboardPaymentDistributionGraph = ({
  selectedMerchant,
  selectedWebsite,
  selectedTimeRange,
  startDate,
  endDate,
  showButton,
  onRemove,
  onShowSnackbar,
  dashboardType,
  tenantType
}) => {

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
    totalAmount: [],
    avatars: [],
  });

  const [loading, setLoading] = useState(false);

  const fetchChartData = useCallback(async () => {
    setLoading(true);

    try {
      let response = null;
      if (tenantType === "pid") {
        response = await getPayCentralPaymentMethodChartDataAction({
          startDate,
          endDate,
          tenantId: selectedMerchant?.pId ? String(selectedMerchant.pId) : "all",
          chartType: selectedTimeRange,
          websiteId: selectedWebsite || null,
        });
      }
      else if (tenantType === "qid") {
        response = await getPaymentMethodChartDataAction({
          startDate,
          endDate,
          merchant: selectedMerchant?.qId || null,
          chartType: selectedTimeRange,
          websiteId: selectedWebsite || null,
        });
      }

      else if (tenantType === "both" || tenantType === "all") {
        response = await getCombinedPaymentMethodChartDataAction({
          startDate,
          endDate,
          tenantId: selectedMerchant?.pId ? String(selectedMerchant.pId) : "all",
          chartType: selectedTimeRange,
          mode: "both", // important
          websiteId: selectedWebsite || null,
        });
      }

      // console.log("FINAL API RESPONSE:", response);

      const apiData = response?.data;

      if (apiData?.labels?.length) {
        setChartData({
          labels: apiData.labels,
          datasets: [
            {
              data: apiData.data,
              backgroundColor: ["#43A047", "#FB8C00", "#1E88E5"],
              borderWidth: 0,
              hoverOffset: 6,
            },
          ],
          totalAmount: apiData.totalAmount || [],
          avatars: apiData.avatars || [],
        });
      } else {
        setChartData({
          labels: [],
          datasets: [
            {
              data: [],
              backgroundColor: ["#43A047", "#FB8C00", "#1E88E5"],
              borderWidth: 0,
              hoverOffset: 6,
            },
          ],
          totalAmount: [],
          avatars: [],
        });
      }

    } catch (error) {
      console.error(error);

      setChartData({
        labels: [],
        datasets: [
          {
            data: [],
            backgroundColor: ["#43A047", "#FB8C00", "#1E88E5"],
          },
        ],
        totalAmount: [],
        avatars: [],
      });

      onShowSnackbar?.(
        error?.response?.data?.message || "Failed to load data",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [
    startDate,
    endDate,
    selectedMerchant,
    selectedWebsite,
    selectedTimeRange,
    tenantType,
    onShowSnackbar,
  ]);

  useEffect(() => {
    if (startDate && endDate && tenantType) {
      fetchChartData();
    }
  }, [startDate, endDate, selectedMerchant, selectedWebsite, tenantType, fetchChartData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
      title: { display: true, text: "Payment Method Distribution" },
    },
  };

  const isNoData =
    !chartData.datasets.length ||
    chartData.datasets.every(ds =>
      !ds.data.length || ds.data.every(v => v === 0)
    );

  return (
    <div className={`kpi-card ${!showButton && "my-3"}`} key="paymentDistributionChart">
      {showButton && (
        <MyCancelIcon
          className="close-icon position-absolute cursor-pointer dash-close z-index-3"
          style={{ top: 5, right: 5 }}
          onClick={() => onRemove("paymentDistributionChart")}
        />
      )}

      <div className="kpi-card-content">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h2 className="chart-title m-0">Payment Method Distribution</h2>
        </div>

        <div className="chart-canvas-container" style={{ height: "350px" }}>
          {loading ? (
            <div className="chart-loading">
              <CircularProgress />
              <p>Loading payment method data...</p>
            </div>
          ) : isNoData ? (
            <NoDataFound message="No payment method data available" />
          ) : (
            <Doughnut data={chartData} options={chartOptions} />
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPaymentDistributionGraph;